import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import AuthApi from "./Services/Authapi";
import ls from "local-storage";
import { isLoginSessionExpired, clearClientAuthState } from "../utils/authSession";

const PermissionCheck = () => {
  const intervalRef = useRef(null);
  const history = useHistory();
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const getLoginRedirectPath = () => {
    try {
      const storedRoute = window.sessionStorage.getItem("postLogoutLoginRoute");
      return ["/login"].includes(storedRoute)
        ? storedRoute
        : "/login";
    } catch (error) {
      return "/login";
    }
  };

  useEffect(() => {
    const checkPermissions = async () => {
      if (
        window.location && 
        (
          window.location.pathname === "/login" ||
          window.location.pathname === "/Forgotpassword"
        )
      ) {
        return;
      }
      const rawToken = ls.get("authToken") || (typeof window !== "undefined" ? window.localStorage.getItem("authToken") : null);
      const hasValidToken = Boolean(
        rawToken &&
        rawToken !== false &&
        rawToken !== "false" &&
        rawToken !== null &&
        rawToken !== "null" &&
        rawToken !== undefined &&
        rawToken !== "undefined" &&
        String(rawToken).trim() !== ""
      );
      if (!hasValidToken) {
        return;
      }

      if (isLoginSessionExpired()) {
        clearClientAuthState();
        try {
          AuthApi.outTime();
          AuthApi.logout();
        } catch (e) {}
        if (window.location && window.location.pathname !== "/login") {
          history.push(getLoginRedirectPath());
        }
        return;
      }

      try {
        const res = await AuthApi.checkAuth();
        if (!res || res.status === false) {
          // Increment retry count before setting authToken to false
          retryCountRef.current += 1;
          
          // Only set authToken to false after multiple consecutive failures
          if (retryCountRef.current >= maxRetries) {
            ls.set("authToken", false);
            try { window.localStorage.removeItem("authToken"); } catch (e) {}
            history.push(getLoginRedirectPath());
            return;
          }
          // Don't redirect on first few failures - just log
          console.warn(`Auth check failed (attempt ${retryCountRef.current}/${maxRetries})`);
          return;
        }
        
        // Reset retry count on successful auth check
        retryCountRef.current = 0;
        
        // Store latest userAccess in local storage with fallback
        const fallbackPermissions = Array.isArray(res.data?.user?.roles)
          ? res.data.user.roles.flatMap((role) =>
              Array.isArray(role?.permissions)
                ? role.permissions
                    .map((permission) => permission?.name)
                    .filter((permissionName) => typeof permissionName === "string" && permissionName.trim() !== "")
                : []
            )
          : [];
        const effectiveAccess =
          Array.isArray(res.data?.userAccess) && res.data.userAccess.length > 0
            ? res.data.userAccess
            : fallbackPermissions;
        if (effectiveAccess && effectiveAccess.length > 0) {
          ls.set("userAccess", effectiveAccess);
        }
      } catch (error) {
        console.error("Error in permission check:", error);
        // Don't immediately set authToken to false on network errors
        retryCountRef.current += 1;
        if (retryCountRef.current >= maxRetries) {
          ls.set("authToken", false);
          try { window.localStorage.removeItem("authToken"); } catch (e) {}
          history.push(getLoginRedirectPath());
        }
      }
    };

    // Initial check
    checkPermissions();

    // Set interval for every 10 minutes (prevents constant /me request spam)
    intervalRef.current = setInterval(checkPermissions, 10 * 60 * 1000);

    // Cleanup on unmount
    return () => clearInterval(intervalRef.current);
  }, [history]);

  return null; // This component does not render anything
};

export default PermissionCheck;

